import { motion } from "framer-motion";

interface BottomTabBarProps {
  currentView: string;
  isChatOpen: boolean;
  onNavigate: (view: string) => void;
  onToggleSettings: () => void;
}

export default function BottomTabBar({ currentView, isChatOpen, onNavigate, onToggleSettings }: BottomTabBarProps) {
  const tabs = [
    {
      id: "lobby",
      label: "首页",
      icon: (active: boolean) => (
        <svg className={`w-6 h-6 ${active ? "text-purple-400" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
    {
      id: "chat",
      label: "聊天",
      icon: (active: boolean) => (
        <svg className={`w-6 h-6 ${active ? "text-purple-400" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
    {
      id: "settings",
      label: "设置",
      icon: (active: boolean) => (
        <svg className={`w-6 h-6 ${active ? "text-purple-400" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  const handleTabClick = (tabId: string) => {
    if (tabId === "chat") {
      // 聊天标签：如果已在聊天中，不做任何事；否则回到首页选择角色
      if (!isChatOpen) {
        onNavigate("lobby");
      }
    } else if (tabId === "settings") {
      // 设置标签：触发设置面板
      onToggleSettings();
    } else {
      onNavigate(tabId);
    }
  };

  const isTabActive = (tabId: string) => {
    if (tabId === "chat") return isChatOpen;
    if (tabId === "settings") return false; // 设置暂时通过其他方式触发
    return currentView === tabId;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
      {/* 背景模糊层 */}
      <div className="absolute inset-0 bg-gray-900/95 backdrop-blur-lg border-t border-purple-500/20" />
      
      {/* 标签内容 */}
      <div className="relative flex items-center justify-around h-16 pb-[env(safe-area-inset-bottom, 0px)]">
        {tabs.map((tab) => {
          const active = isTabActive(tab.id);
          return (
            <motion.button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              whileTap={{ scale: 0.9 }}
              className="flex flex-col items-center justify-center flex-1 h-full min-w-[44px] touch-manipulation"
            >
              {/* 图标 */}
              <motion.div
                animate={active ? { scale: 1.1 } : { scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                {tab.icon(active)}
              </motion.div>
              
              {/* 标签文字 */}
              <span className={`text-xs mt-1 ${active ? "text-purple-400 font-medium" : "text-gray-400"}`}>
                {tab.label}
              </span>
              
              {/* 激活指示器 */}
              {active && (
                <motion.div
                  layoutId="bottom-tab-indicator"
                  className="absolute bottom-1 w-8 h-1 bg-purple-400 rounded-full"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
